import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Email as EmailIcon } from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import RolesTab from './Tabs/RolesTab';
import LeaveAllocationTab from './Tabs/LeaveAllocationTab';
import ActivitiesTab from './Tabs/ActivitiesTab';
import PeriodsTab from './Tabs/PeriodsTab';
import AppConfigTab from './Tabs/AppConfigTab';
import SessionsTab from './Tabs/SessionsTab';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import axios from 'axios';


const PromotionTab = () => null;

const Setup = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isSuperAdmin } = useAuth();

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const [tab, setTab] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [adModalOpen, setAdModalOpen] = useState(false);
  const [adRecipientEmail, setAdRecipientEmail] = useState('');
  const [adRecipientName, setAdRecipientName] = useState('');
  const [adSchoolName, setAdSchoolName] = useState('');
  const drawerWidth = 280;
  const addButtonRefs = useRef({});

  // Define all tabs with their properties
  const allTabs = useMemo(() => [
    { id: 0, label: 'Roles', component: RolesTab, superAdminOnly: true },
    { id: 1, label: 'Sessions', component: SessionsTab, superAdminOnly: true },
    { id: 2, label: 'Leave Allocation', component: LeaveAllocationTab, superAdminOnly: false },
    { id: 3, label: 'Holidays & Events', component: ActivitiesTab, superAdminOnly: false },
    { id: 4, label: 'Class Periods', component: PeriodsTab, superAdminOnly: false },
    { id: 5, label: 'Configuration', component: AppConfigTab, superAdminOnly: true },
  ], []);

  // Filter tabs based on user role
  const visibleTabs = useMemo(() => {
    return allTabs.filter(tab => !tab.superAdminOnly || isSuperAdmin());
  }, [allTabs, isSuperAdmin]);

  // Map visible tab index to actual tab id
  const getActualTabId = (visibleIndex) => {
    return visibleTabs[visibleIndex]?.id ?? 0;
  };

  // Map actual tab id to visible tab index
  const getVisibleTabIndex = (actualId) => {
    return visibleTabs.findIndex(tab => tab.id === actualId);
  };

  const handleTabChange = (e, visibleIndex) => {
    const actualId = getActualTabId(visibleIndex);
    setTab(actualId);
  };

  // Get current visible tab index
  const visibleTabIndex = useMemo(() => {
    const index = visibleTabs.findIndex(t => t.id === tab);
    return index >= 0 ? index : 0;
  }, [tab, visibleTabs]);

  // Ensure tab is valid when visible tabs change
  useEffect(() => {
    const index = visibleTabs.findIndex(t => t.id === tab);
    if (index < 0 && visibleTabs.length > 0) {
      // If current tab is not visible, switch to first visible tab
      setTab(visibleTabs[0].id);
    }
  }, [visibleTabs, tab]);

  const handleAddClick = () => {
    if (addButtonRefs.current[tab]) {
      addButtonRefs.current[tab]();
    }
  };

  const getAddButtonLabel = () => {
    const labels = {
      0: 'Add Role',
      1: 'Add Session',
      2: 'Add Allocation',
      3: 'Add Activity',
      4: 'Add Period',
      5: 'Add Configuration',
    };
    return labels[tab] || 'Add';
  };

  const handleSendAdvertisingEmail = async () => {
    // Basic validation if manual email provided
    if (adRecipientEmail.trim()) {
      // Use standard whitespace-aware pattern (no double escaping)
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(adRecipientEmail.trim())) {
        showToast.error('Please enter a valid recipient email');
        return;
      }
    }

    try {
      setSendingEmail(true);
      const payload = {
        recipient_email: adRecipientEmail.trim(),
        recipient_name: adRecipientName.trim(),
        school_name: adSchoolName.trim(),
      };

      const response = await axios.post('https://api.ptshub.in/advertising/send.php', payload);
      
      if (response.data.success) {
        showToast.success(response.data.message || 'Advertising email sent successfully to schools');
        setAdModalOpen(false);
        setAdRecipientEmail('');
        setAdRecipientName('');
        setAdSchoolName('');
      } else {
        showToast.error(response.data.error || 'Failed to send advertising email');
      }
    } catch (err) {
      console.error('Error sending advertising email:', err);
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to send advertising email';
      showToast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              px: { xs: 1, sm: 2, md: 3 },
              pt: { xs: 1.5, sm: 2 },
              pb: { xs: 2, sm: 3 },
              mt: { xs: '56px', sm: '64px' },
              minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
              backgroundColor: 'background.default',
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 1.5, sm: 2 }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'stretch', sm: 'center' }} 
              sx={{ mb: { xs: 2, sm: 3 } }}
            >
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    wordBreak: 'break-word',
                  }}
                >
                  Setup & Configuration
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  size={isMobile ? 'medium' : 'small'}
                  fullWidth={isMobile}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 'auto' },
                    mt: { xs: 0, sm: 0 },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getAddButtonLabel()}
                </Button>
                {tab === 5 && isSuperAdmin() && (
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={() => setAdModalOpen(true)}
                    size={isMobile ? 'medium' : 'small'}
                    fullWidth={isMobile}
                    disabled={sendingEmail}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 'auto' },
                      mt: { xs: 0, sm: 0 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Advertising Email'}
                  </Button>
                )}
              </Stack>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                mb: { xs: 2, sm: 3 },
                overflow: 'hidden',
                boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.04)' },
                width: '100%',
                maxWidth: '100vw',
              }}
            >
              <Tabs
                value={visibleTabIndex}
                onChange={handleTabChange}
                aria-label="Setup Tabs"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: { xs: 48, sm: 64 },
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  '& .MuiTabs-scrollButtons': {
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                    width: { xs: 32, sm: 48 },
                  },
                  '& .MuiTabs-flexContainer': {
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none',
                  },
                }}
                TabIndicatorProps={{
                  sx: {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    bgcolor: 'primary.main',
                  },
                }}
              >
                {visibleTabs.map((tabConfig) => (
                <Tab
                    key={tabConfig.id}
                    label={tabConfig.label}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    px: { xs: 1.5, sm: 2, md: 3 },
                    py: { xs: 1.5, sm: 2 },
                    minHeight: { xs: 48, sm: 64 },
                    color: 'text.secondary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700,
                    },
                  }}
                />
                ))}
              </Tabs>
            </Paper>
            
            {visibleTabs.map((tabConfig) => {
              const TabComponent = tabConfig.component;
              return (
            <Box 
                  key={tabConfig.id}
                  hidden={tab !== tabConfig.id} 
              role="tabpanel"
              sx={{ 
                width: '100%',
                    maxWidth: tabConfig.id === 0 ? '100vw' : tabConfig.id === 1 ? '90vw' : '100%',
                mx: 'auto',
                overflowX: 'hidden',
                overflowY: 'auto',
                '& > *': {
                  minWidth: 0,
                  maxWidth: '100%',
                },
              }}
            >
                  {tab === tabConfig.id && (
                    <TabComponent onAddRef={(fn) => { addButtonRefs.current[tabConfig.id] = fn; }} />
                  )}
            </Box>
              );
            })}

            {/* Advertising Email Modal */}
            <Dialog
              open={adModalOpen}
              onClose={() => setAdModalOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 700 }}>Send Advertising Email</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <TextField
                    label="School / Recipient Name"
                    value={adRecipientName}
                    onChange={(e) => setAdRecipientName(e.target.value)}
                    placeholder="e.g., Green Valley School"
                    fullWidth
                  />
                  <TextField
                    label="Recipient Email"
                    value={adRecipientEmail}
                    onChange={(e) => setAdRecipientEmail(e.target.value)}
                    placeholder="e.g., principal@school.com"
                    type="email"
                    required
                    fullWidth
                  />
                  <TextField
                    label="Product / School Name (optional)"
                    value={adSchoolName}
                    onChange={(e) => setAdSchoolName(e.target.value)}
                    placeholder="e.g., PTS Hub"
                    fullWidth
                  />
                  <Typography variant="body2" color="text.secondary">
                    Leave fields empty to send to all interested contacts. Provide name and email to send to a specific school.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAdModalOpen(false)} color="inherit">
                  Cancel
                </Button>
                <Button
                  onClick={handleSendAdvertisingEmail}
                  variant="contained"
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Sending...' : 'Send'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Setup;


